/**
 * @author      yewon@zumper.com (Yewon Lee)
 * @copyright   Copyright (c) 2018, Zumper
 * @description selectBreadcrumbFromCurrentSearch
 */

import { getFiltersFromUrlFiltersPath } from '@zcode-ui/filters'
import { getDetailHref } from '@zcode-ui/web-zumper-global-state'
import { memoizeStateByRouteProps } from '@zcode/memoizers'
import { toCityUrl } from '@zcode/nr-utils'
import { hasHasGalleryParam } from '@zcode/nr/es/shared/bizos/rentable/selectors'
import { NEW_YORK_BOROUGHS_URLS } from '@zcode/nr/es/shared/components/Autocomplete/constants'
import { toString } from '@zcode/nr/es/shared/utils/utils'
import { findMatch } from '@zcode/router-utils'
import type { RouteComponentProps } from '@zcode/router-utils'
import type {
  Building,
  City,
  Listing,
  MCity,
  Neighborhood,
  State,
  VerticalsAllowList,
} from '@zcode/state-type'
import { rootRelative } from '@zcode/utils'
import { currentSearchRoutes, routesConstants } from '@zcode/zumper-routes'
import { getPrimaryFacetFromFiltersPath } from '../../../../../utils/urls/getPrimaryFacetFromFiltersPath'
import { shouldFiltersOmitRelCanonical } from '../../../../../utils/urls/shouldFiltersOmitRelCanonical'
import { toHoodUrl } from '../../../../../utils/urls/toHoodUrl'
import { type PropertyTypePath, getBaseUrlFromPropertyType, getPropertyTypeLabel } from './helpers'

interface GetCityOrHoodUrlProps {
  cityName: string | null | undefined
  hoodName: string | null | undefined
  propertyTypePath: PropertyTypePath | undefined
  stateName: string | null | undefined
}

const getCityOrHoodUrl = ({
  cityName,
  hoodName,
  propertyTypePath,
  stateName,
}: GetCityOrHoodUrlProps) => {
  let url: '' | ReturnType<typeof toCityUrl> | ReturnType<typeof toHoodUrl> | null = ''
  if (hoodName && cityName) {
    url = toHoodUrl({
      basePath: propertyTypePath,
      city: cityName,
      hood: hoodName,
      state: stateName,
    })
  } else if (cityName) {
    url = toCityUrl({
      basePath: propertyTypePath,
      city: cityName,
      state: stateName,
    })
  }
  return url
}

export interface SelectBreadcrumbFromCurrentSearch {
  state: Pick<State['currentSearch']['location'], 'cityName' | 'hoodName' | 'stateName' | 'url'>
  currentCity: City | MCity | null
  currentHood: Neighborhood | null
  entity?: Building | Listing
  cityVerticals?: VerticalsAllowList
  hoodVerticals?: VerticalsAllowList
}

export const labelToVertical = (label: 'Rooms' | 'Houses' | 'Apartments' | 'Condos') => {
  if (label === 'Rooms') {
    return 'rooms'
  }
  if (label === 'Houses') {
    return 'houses'
  }
  if (label === 'Apartments') {
    return 'apartments'
  }
  if (label === 'Condos') {
    return 'condos'
  }
  return 'apartments'
}
export const baseUrlPathToVertical = (
  baseUrlPath: 'apartments-for-rent' | 'rooms-for-rent' | 'houses-for-rent' | 'condos-for-rent'
) => {
  if (baseUrlPath === 'apartments-for-rent') {
    return 'apartments'
  }
  if (baseUrlPath === 'rooms-for-rent') {
    return 'rooms'
  }
  if (baseUrlPath === 'condos-for-rent') {
    return 'condos'
  }
  if (baseUrlPath === 'houses-for-rent') {
    return 'houses'
  }
  return 'apartments'
}

export const selectBreadcrumbFromCurrentSearch = (
  {
    state,
    currentCity,
    currentHood,
    entity,
    cityVerticals,
    hoodVerticals,
  }: SelectBreadcrumbFromCurrentSearch,
  routeProps: Pick<RouteComponentProps, 'location'> | undefined
): readonly Breadcrumb[] => {
  const breadcrumb: Breadcrumb[] = [
    {
      name: 'Home',
      // use schemaName to update the ld+json name field
      schemaName: 'Homepage',
      url: '/',
      isIndexed: true,
    },
  ]
  if (!(state || entity)) {
    return breadcrumb
  }

  const {
    cityName: locationCityName,
    hoodName: locationHoodName,
    stateName: locationStateName,
    url: locationUrl,
  } = state
  const cityState = entity?.cityState
  const fallbackCityName = currentHood?.city || currentCity?.name || entity?.city
  const fallbackHoodName = currentHood?.name || entity?.neighborhoodName
  const fallbackStateName = currentHood?.state || currentCity?.state || entity?.state
  const fallbackUrl = currentHood?.url || currentCity?.url
  const hoodName = locationHoodName || fallbackHoodName
  const cityName = locationCityName || fallbackCityName
  const stateName = locationStateName || fallbackStateName
  const url = locationUrl || fallbackUrl
  let filtersPath
  let propertyTypePath: PropertyTypePath | undefined

  if (routeProps) {
    const { location } = routeProps
    const match = findMatch<Record<string, string | undefined>>(currentSearchRoutes, location)
    const params = match && match.params
    if (params) {
      filtersPath = params.filtersPath
      // @ts-expect-error string isn't assignable to `${...}-for-rent`
      propertyTypePath = params.propertyTypePath
    }
  }

  const isNearCity = url?.includes?.('near')

  // the order will be
  // homepage > afr/new-york-ny (if cityState is a borough) > afr/city-state > afr/city-state/hood > Xfr/city-state/hood > Xfr/city-state/hood/facet
  const propertyType = entity?.propertyType
  const baseUrlPath = getBaseUrlFromPropertyType({ propertyType, propertyTypePath })
  // @ts-expect-error: It doesn't like ['some', 'strings'].includes(null)
  const isNYCBorough = Object.values(NEW_YORK_BOROUGHS_URLS).includes(cityState)
  if (isNYCBorough) {
    let cityCrumbName = 'New York'
    let isIndexed: boolean | undefined = false
    if (entity) {
      const propertyTypeLabel = getPropertyTypeLabel(propertyType)
      cityCrumbName = `${propertyTypeLabel} in New York`
      isIndexed = cityVerticals?.[labelToVertical(propertyTypeLabel)]?.includes('/')
    } else {
      if (propertyTypePath === routesConstants.APTS_BASE_URL) {
        cityCrumbName = 'Apartments in New York'
        isIndexed = cityVerticals?.apartments?.includes('/')
      } else if (propertyTypePath === routesConstants.CONDOS_BASE_URL) {
        cityCrumbName = 'Condo Rentals in New York'
        isIndexed = cityVerticals?.condos?.includes('/')
      } else if (propertyTypePath === routesConstants.HOUSES_BASE_URL) {
        cityCrumbName = 'House Rentals in New York'
        isIndexed = cityVerticals?.houses?.includes('/')
      } else if (propertyTypePath === routesConstants.ROOMS_BASE_URL) {
        cityCrumbName = 'Room Rentals in New York'
        isIndexed = cityVerticals?.rooms?.includes('/')
      }
    }
    breadcrumb.push({
      name: cityCrumbName,
      schemaName: cityCrumbName,
      url: rootRelative(toCityUrl({ cityState: 'new-york-ny', basePath: baseUrlPath })),
      isIndexed,
    })
  }
  if (cityName) {
    let cityCrumbName = cityName
    let isIndexed: boolean | undefined = false
    if (entity) {
      const propertyTypeLabel = getPropertyTypeLabel(propertyType)
      cityCrumbName = `${propertyTypeLabel} in ${cityCrumbName}`
      isIndexed = cityVerticals?.[labelToVertical(propertyTypeLabel)]?.includes('/')
    } else {
      if (propertyTypePath === routesConstants.APTS_BASE_URL) {
        cityCrumbName = `Apartments in ${cityCrumbName}`
        isIndexed = cityVerticals?.apartments?.includes('/')
      } else if (propertyTypePath === routesConstants.CONDOS_BASE_URL) {
        cityCrumbName = `Condo Rentals in ${cityCrumbName}`
        isIndexed = cityVerticals?.condos?.includes('/')
      } else if (propertyTypePath === routesConstants.HOUSES_BASE_URL) {
        cityCrumbName = `House Rentals in ${cityCrumbName}`
        isIndexed = cityVerticals?.houses?.includes('/')
      } else if (propertyTypePath === routesConstants.ROOMS_BASE_URL) {
        cityCrumbName = `Room Rentals in ${cityCrumbName}`
        isIndexed = cityVerticals?.rooms?.includes('/')
      }
    }
    const nearUrl = isNearCity && propertyTypePath ? `${routesConstants.APTS_BASE_URL}/${url}` : ''
    const cityUrl = toCityUrl({ city: cityName, state: stateName, basePath: baseUrlPath })
    breadcrumb.push({
      name: cityCrumbName,
      // use schemaName to update the ld+json name field
      schemaName: cityCrumbName,
      url: rootRelative(nearUrl || cityUrl),
      isIndexed,
    })
  }
  if (hoodName) {
    const hoodUrl = toHoodUrl({
      city: cityName,
      hood: hoodName,
      state: stateName,
      basePath: baseUrlPath,
    })
    const isIndexed = hoodVerticals?.[baseUrlPathToVertical(baseUrlPath)]?.includes('/')
    breadcrumb.push({
      name: hoodName,
      // use schemaName to update the ld+json name field
      schemaName: hoodName,
      url: rootRelative(hoodUrl),
      isIndexed,
    })
  }
  if (filtersPath && !entity) {
    const pathFilters = getFiltersFromUrlFiltersPath(filtersPath)
    const canonicalPathFilters = pathFilters
    let primaryFacetPath: string | undefined
    if (!shouldFiltersOmitRelCanonical(canonicalPathFilters)) {
      primaryFacetPath = getPrimaryFacetFromFiltersPath(canonicalPathFilters)
    }
    if (primaryFacetPath) {
      const url = getCityOrHoodUrl({ cityName, hoodName, propertyTypePath, stateName })
      const primaryFacetUrl = `${url}${primaryFacetPath}`
      const primaryFacetName = toString(primaryFacetPath.replace(/[/]/g, ''))
      const verticals = hoodName ? hoodVerticals : cityVerticals
      const isIndexed =
        propertyTypePath &&
        verticals?.[baseUrlPathToVertical(propertyTypePath)]?.includes(primaryFacetPath)

      breadcrumb.push({
        name: primaryFacetName,
        // use schemaName to update the ld+json name field
        schemaName: primaryFacetName,
        url: rootRelative(primaryFacetUrl),
        isIndexed,
      })
    }
  }
  if (entity?.formattedAddress) {
    let entityName = entity.formattedAddress
    if (entity.isBuilding && entity.name) {
      entityName = entity.name
    }

    const detailHref = getDetailHref(entity)
    if (detailHref) {
      breadcrumb.push({ name: entityName, schemaName: entityName, url: detailHref })
    }
    if (hasHasGalleryParam(routeProps?.location?.search)) {
      breadcrumb.push({ name: 'Gallery', schemaName: 'Gallery', url: `${entity.url}?gallery` })
    }
  }
  return breadcrumb
}

export const selectBreadcrumbFromCurrentSearchMemo = memoizeStateByRouteProps(
  selectBreadcrumbFromCurrentSearch
)

export interface Breadcrumb {
  readonly name: string
  readonly rel?: string
  readonly schemaName: string
  readonly target?: string
  readonly url: string
  readonly isIndexed?: boolean
}
